/**
 * Tags de conversa (atendimento): chips na lista e no topo do chat, e o editor que o atendente
 * usa para classificar. A classificação serve ao CRM (filtrar/segmentar), por isso as sugestões
 * são os rótulos comerciais mais comuns; qualquer texto curto também vale.
 *
 * As tags vêm de `/api/channels/tags` (`useConversationTags`). Rota ausente → `tags === null` e
 * o editor declara "aguardando contrato" em vez de simular que salvou.
 */
import React, { useState, type ReactElement } from "react";
import { Plus, Tag, X } from "lucide-react";
import { useSetConversationTags } from "../queries.js";
import { Badge, type BadgeTone } from "../ui/index.js";
import { controlClass } from "../ui/index.js";

export const SUGGESTED_TAGS = ["Orçamento", "Pós-venda", "Garantia", "Revenda", "Urgente", "Financeiro", "Suporte técnico", "VIP"] as const;

const TONES: BadgeTone[] = ["brand", "success", "warning", "info", "danger", "neutral"];

/** Cor estável por texto: a mesma tag tem sempre o mesmo tom em qualquer tela. */
export function tagTone(tag: string): BadgeTone {
  let hash = 0;
  for (const char of tag.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return TONES[hash % TONES.length] ?? "neutral";
}

export function TagChips({ tags, max = 3 }: { tags: readonly string[]; max?: number }): ReactElement | null {
  if (tags.length === 0) return null;
  const shown = tags.slice(0, max);
  const hidden = tags.length - shown.length;
  return (
    <span className="flex flex-wrap items-center gap-1" data-conversation-tags={tags.join("|")}>
      {shown.map((tag) => (
        <Badge key={tag} tone={tagTone(tag)} data-tag={tag}>
          {tag}
        </Badge>
      ))}
      {hidden > 0 ? <Badge tone="neutral">{`+${hidden}`}</Badge> : null}
    </span>
  );
}

export function TagEditor({ id, tags, available }: { id: string; tags: readonly string[]; available: boolean }): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        data-action="edit-tags"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex min-h-[32px] items-center gap-1 rounded-pill border border-dashed border-border-strong px-2.5 text-xs font-bold text-muted hover:border-ink hover:text-ink"
      >
        <Tag className="h-3.5 w-3.5" aria-hidden focusable={false} />
        {tags.length === 0 ? "Adicionar tag" : "Editar tags"}
      </button>
      {open ? <TagPopover id={id} tags={tags} available={available} /> : null}
    </div>
  );
}

/** Conteúdo do editor: só monta (e só usa a API) com o painel aberto. */
function TagPopover({ id, tags, available }: { id: string; tags: readonly string[]; available: boolean }): ReactElement {
  const [draft, setDraft] = useState("");
  const save = useSetConversationTags();
  const apply = (next: string[]): void => save.mutate({ id, tags: next });
  const toggle = (tag: string): void => apply(tags.includes(tag) ? tags.filter((item) => item !== tag) : [...tags, tag]);
  return (
    <>
      {(
        <div role="dialog" aria-label="Tags da conversa" className="absolute left-0 top-full z-20 mt-2 flex w-72 flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s2">
          {available ? (
            <>
              {tags.length > 0 ? (
                <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" aria-label="Tags aplicadas">
                  {tags.map((tag) => (
                    <li key={tag}>
                      <button type="button" onClick={() => toggle(tag)} aria-label={`Remover tag ${tag}`} className="inline-flex items-center gap-1 rounded-pill bg-brand-wash px-2.5 py-0.5 text-xs font-bold text-brand-text">
                        {tag}
                        <X className="h-3 w-3" aria-hidden focusable={false} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="flex flex-wrap gap-1.5" aria-label="Sugestões de tag">
                {SUGGESTED_TAGS.filter((tag) => !tags.includes(tag)).map((tag) => (
                  <button key={tag} type="button" onClick={() => toggle(tag)} className="inline-flex items-center gap-1 rounded-pill border border-border px-2.5 py-0.5 text-xs font-bold text-ink hover:border-ink">
                    <Plus className="h-3 w-3" aria-hidden focusable={false} />
                    {tag}
                  </button>
                ))}
              </div>
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const value = draft.trim();
                  if (!value || value.length > 24 || tags.includes(value)) return;
                  apply([...tags, value]);
                  setDraft("");
                }}
              >
                <input aria-label="Nova tag" value={draft} onChange={(event) => setDraft(event.target.value)} maxLength={24} placeholder="Outra tag…" className={controlClass} />
                <button type="submit" className="rounded-control bg-brand px-3 text-sm font-bold text-on-brand">
                  Criar
                </button>
              </form>
              {save.error ? <p className="m-0 text-xs text-danger" role="alert">{`Não foi possível salvar: ${save.error.message}`}</p> : null}
            </>
          ) : (
            <p className="m-0 text-sm text-muted" data-status="awaiting_source">
              Tags aguardando contrato: a rota /api/channels/tags não respondeu neste ambiente.
            </p>
          )}
        </div>
      )}
    </>
  );
}
